import type { LucideIcon } from 'lucide-react';
import { Binary } from 'lucide-react';
import type { IconName } from 'tech-stack-icons';

/**
 * Maps package types to tech-stack-icons icon names.
 * Primary icon source covering the majority of package types.
 */
export const STACK_ICON_BY_PACKAGE_TYPE: Record<string, IconName> = {
  bitnami: 'bitnami',
  conan: 'c++',
  'dart-pub': 'dart',
  dotnet: 'nuget',
  'erlang-otp': 'erlang',
  gem: 'ruby',
  'github-action': 'github',
  'github-action-workflow': 'github',
  'go-module': 'go',
  'graalvm-native-image': 'java',
  hackage: 'haskell',
  hex: 'elixir',
  homebrew: 'homebrew',
  'java-archive': 'java',
  'linux-kernel': 'linux',
  'linux-kernel-module': 'linux',
  'lua-rocks': 'lua',
  model: 'huggingface',
  'msrc-kb': 'windows11',
  nix: 'nixos',
  npm: 'npm2',
  opam: 'ocaml',
  pod: 'swift',
  portage: 'linux',
  python: 'python',
  'R-package': 'r',
  rpm: 'redhat',
  'rust-crate': 'rust',
  swift: 'swift',
  swiplpack: 'prolog',
  terraform: 'terraform',
  'wordpress-plugin': 'wordpress',
};

/**
 * Maps package types to Lucide icon components.
 * Used for types where Lucide provides a better fit than tech-stack-icons.
 */
export const LUCIDE_ICON_BY_PACKAGE_TYPE: Record<string, LucideIcon> = {
  binary: Binary,
  conda: Binary,
};

/**
 * Maps package types to Font Awesome icon class names.
 */
export const FA_ICON_BY_PACKAGE_TYPE: Record<string, string> = {
  'jenkins-plugin': 'jenkins icon',
  'php-composer': 'php icon',
  'php-pear': 'php icon',
  'php-pecl': 'php icon',
};

/**
 * Maps package types to Font Mfizz icon class names.
 * Used for Linux distros not available in tech-stack-icons.
 */
export const MFIZZ_ICON_BY_PACKAGE_TYPE: Record<string, string> = {
  alpm: 'icon-archlinux',
  apk: 'icon-alpinelinux',
  deb: 'icon-debian',
};

/**
 * Maps package type identifiers to human-readable display labels.
 */
export const LABEL_BY_PACKAGE_TYPE: Record<string, string> = {
  alpm: 'ALPM',
  apk: 'Alpine',
  binary: 'Binary',
  bitnami: 'Bitnami',
  conan: 'Conan',
  conda: 'Conda',
  'dart-pub': 'Dart Pub',
  deb: 'Debian',
  dotnet: '.NET',
  'erlang-otp': 'Erlang/OTP',
  gem: 'Ruby Gem',
  'github-action': 'GitHub Action',
  'github-action-workflow': 'GitHub Action Workflow',
  'go-module': 'Go',
  'graalvm-native-image': 'GraalVM Native Image',
  hackage: 'Hackage',
  hex: 'Hex',
  homebrew: 'Homebrew',
  'java-archive': 'Java',
  'jenkins-plugin': 'Jenkins Plugin',
  'linux-kernel': 'Linux Kernel',
  'linux-kernel-module': 'Linux Kernel Module',
  'lua-rocks': 'LuaRocks',
  model: 'Model',
  'msrc-kb': 'Windows KB',
  nix: 'Nix',
  npm: 'npm',
  opam: 'opam',
  'php-composer': 'PHP Composer',
  'php-pear': 'PHP PEAR',
  'php-pecl': 'PHP PECL',
  pod: 'CocoaPods',
  portage: 'Portage',
  python: 'Python',
  'R-package': 'R',
  rpm: 'RPM',
  'rust-crate': 'Rust',
  swift: 'Swift',
  swiplpack: 'SWI-Prolog',
  terraform: 'Terraform',
  'wordpress-plugin': 'WordPress',
};

/**
 * Union of all known package type identifiers.
 */
export type PackageType = keyof typeof LABEL_BY_PACKAGE_TYPE;

/**
 * Array of all known package type identifiers, derived from the label map.
 */
export const PACKAGE_TYPES = Object.keys(
  LABEL_BY_PACKAGE_TYPE,
) as PackageType[];

/**
 * Maps legacy package type keys (from older APIs) to their current equivalents.
 */
export const LEGACY_PACKAGE_TYPE_MAP: Record<string, string> = {
  apkg: 'apk',
  dpkg: 'deb',
  go: 'go-module',
  java: 'java-archive',
  kb: 'msrc-kb',
  maven: 'java-archive',
  nuget: 'dotnet',
  pypi: 'python',
};

/**
 * Resolves a package type string through the legacy map.
 * Returns the modern equivalent if a legacy key is provided, otherwise
 * returns the input unchanged.
 */
export const resolvePackageType = (type: string): string =>
  LEGACY_PACKAGE_TYPE_MAP[type] ?? type;
